/*
  InPost delivery selection in the cart.

  The choice is stored as cart attributes via /cart/update.js. Attributes survive
  checkout and land on the order, so the method and locker id are visible when
  fulfilling — no middleman app required.

  Guiding rule: FAIL OPEN. The checkout lock only engages when the customer has
  deliberately picked a locker delivery and not chosen a point. Any error along
  the way releases the lock, because a dead "Realizuj zakup" button on a live
  store costs more than an order missing a locker id.

  Geowidget is loaded lazily on first map open — InPost's script and stylesheet
  are heavy, and a good share of carts ship by courier anyway.
*/
(function () {
  var GEOWIDGET_CSS = 'https://geowidget.inpost.pl/inpost-geowidget.css';
  var GEOWIDGET_JS = 'https://geowidget.inpost.pl/inpost-geowidget.js';
  var LOCKER = 'InPost Paczkomat';
  var COURIER = 'InPost Kurier';

  var loading = null;

  // The cart can exist as a page and a drawer at the same time, so there may be
  // more than one instance. Each needs its own callback name: Geowidget calls it
  // by name off the global scope, so a shared name means the last instance
  // silently overwrites the previous ones.
  var instanceCount = 0;

  function widgets() {
    return Array.prototype.slice.call(document.querySelectorAll('[data-gz-delivery]'));
  }

  function selectedPoint() {
    var el = document.querySelector('[data-gz-code]');
    return el ? el.textContent.trim() : '';
  }

  // Consent is a hard requirement, unlike the locker choice which only applies to
  // one delivery method. Stored as a cart attribute as well as a checkbox state:
  // it then survives the cart re-rendering and leaves a record on the order.
  function consentBoxes() {
    return Array.prototype.slice.call(document.querySelectorAll('[data-gz-consent]'));
  }

  function consentGiven() {
    var boxes = consentBoxes();
    if (!boxes.length) return true; // no policies published, nothing to accept
    return boxes.every(function (b) {
      return b.checked;
    });
  }

  function lockerMode() {
    var checked = document.querySelector('[data-gz-method]:checked');
    return !!checked && checked.value === 'paczkomat';
  }

  function saveAttributes(attributes) {
    return fetch(window.Shopify.routes.root + 'cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: attributes }),
    }).then(function (r) {
      if (!r.ok) throw new Error('Koszyk odrzucił zapis wyboru.');
      return r.json();
    });
  }

  /* ── checkout lock ────────────────────────────────────────────────────── */

  function checkoutButtons() {
    return Array.prototype.slice.call(
      // The drawer no longer checks out — its action leads to the cart page —
      // so the only buttons to lock are the ones on the cart page itself.
      document.querySelectorAll('#checkout, [name="checkout"]')
    );
  }

  function setLock(locked) {
    // The flag lives on <body>, not on the button. Dawn replaces the checkout
    // button on every cart update, and a class set on the button itself was gone
    // for the frames between that swap and the next refresh — the button flashed
    // back to its active colour on every quantity change. The body survives the
    // swap, so the locked styling never lapses.
    document.body.classList.toggle('gz-locked', !!locked);
    checkoutButtons().forEach(function (b) {
      b.disabled = !!locked;
    });
    // Shop Pay / PayPal / Google Pay skip the cart and jump straight to checkout,
    // so the lock has to reach them too. They are third-party iframes and cannot
    // be `disabled`, so they are made `inert` — which also takes them out of the
    // tab order, unlike pointer-events alone. They stay visible but greyed:
    // payment buttons that vanish read as a broken page, not as a requirement.
    document.querySelectorAll('.additional-checkout-buttons').forEach(function (el) {
      el.inert = !!locked;
    });
    widgets().forEach(function (el) {
      var notice = el.querySelector('[data-gz-required]');
      if (notice) notice.hidden = !locked;
    });
  }

  function refreshLock() {
    try {
      var missingPoint = lockerMode() && !selectedPoint();
      var missingConsent = !consentGiven();
      setLock(missingPoint || missingConsent);
      // Only flag the checkbox once it is the thing standing in the way, so it
      // does not shout at someone who has not reached it yet.
      consentBoxes().forEach(function (b) {
        var label = b.closest('.gz-consent');
        if (label) label.classList.toggle('gz-consent--missing', missingConsent && !missingPoint);
      });
    } catch (e) {
      setLock(false); // when in doubt, let the customer through
    }
  }

  /* ── visible state ────────────────────────────────────────────────────── */

  // Page and drawer are the same cart, so a choice made in one has to show in
  // every instance.
  function renderPoint(code, address) {
    widgets().forEach(function (el) {
      var codeEl = el.querySelector('[data-gz-code]');
      var addressEl = el.querySelector('[data-gz-address]');
      var chosen = el.querySelector('[data-gz-chosen]');
      var open = el.querySelector('[data-gz-open]');
      if (codeEl) codeEl.textContent = code;
      if (addressEl) addressEl.textContent = address;
      if (chosen) chosen.hidden = !code;
      if (open) open.textContent = code ? 'Zmień paczkomat' : 'Wybierz paczkomat na mapie';
    });
    refreshLock();
  }

  var PLN = new Intl.NumberFormat('pl-PL', { style: 'currency', currency: 'PLN' });

  // Shipping only becomes real at checkout, once there is an address. Until then
  // the figure is mirrored from the delivery profile via data attributes, so the
  // customer sees what they will actually pay rather than "calculated later".
  function renderCosts() {
    var locker = lockerMode();
    widgets().forEach(function (el) {
      var free = el.dataset.darmowa === '1';
      var goods = parseInt(el.dataset.sumaProduktow, 10) || 0;
      var threshold = parseInt(el.dataset.progDarmowej, 10) || 0;
      var lockerRate = parseInt(el.dataset.kosztPaczkomat, 10) || 0;
      var courierRate = parseInt(el.dataset.kosztKurier, 10) || 0;
      var shipping = free ? 0 : locker ? lockerRate : courierRate;

      function set(sel, text) {
        var node = el.querySelector(sel);
        if (node) node.textContent = text;
      }

      set('[data-gz-price-locker]', free ? 'Gratis' : PLN.format(lockerRate / 100));
      set('[data-gz-price-courier]', free ? 'Gratis' : PLN.format(courierRate / 100));
      set('[data-gz-goods]', PLN.format(goods / 100));
      set('[data-gz-shipping-cost]', free ? 'Gratis' : PLN.format(shipping / 100));
      set('[data-gz-total]', PLN.format((goods + shipping) / 100));
      set('[data-gz-missing]', PLN.format(Math.max(threshold - goods, 0) / 100));

      var note = el.querySelector('[data-gz-threshold]');
      if (note) note.hidden = free;
    });
  }

  // Dawn only swaps `.js-contents` inside the cart footer when the cart changes,
  // and this block sits outside it — so its figures would keep showing whatever
  // the page was rendered with. Re-reading the cart is cheaper and more reliable
  // than trying to parse the replaced markup.
  function syncFromCart() {
    // `no-store` is not optional here: /cart.js is served cacheable, so without
    // it the refresh kept reading the basket as it was before the change and the
    // panel stayed one step behind every click.
    return fetch(window.Shopify.routes.root + 'cart.js', { cache: 'no-store' })
      .then(function (r) {
        return r.json();
      })
      .then(function (cart) {
        widgets().forEach(function (el) {
          var threshold = parseInt(el.dataset.progDarmowej, 10) || 0;
          el.dataset.sumaProduktow = cart.total_price;
          el.dataset.darmowa = cart.total_price >= threshold ? '1' : '0';
        });
        renderCosts();
      })
      .catch(function () {
        /* leave the last known figures rather than blanking the panel */
      });
  }

  function renderPanels() {
    var locker = lockerMode();
    widgets().forEach(function (el) {
      var panel = el.querySelector('[data-gz-panel]');
      if (panel) panel.hidden = !locker;
    });
    renderCosts();
    refreshLock();
  }

  /* ── Geowidget ────────────────────────────────────────────────────────── */

  function loadGeowidget() {
    if (loading) return loading;
    loading = new Promise(function (resolve, reject) {
      if (!document.querySelector('link[href="' + GEOWIDGET_CSS + '"]')) {
        var link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = GEOWIDGET_CSS;
        document.head.appendChild(link);
      }
      var script = document.createElement('script');
      script.src = GEOWIDGET_JS;
      script.defer = true;
      script.onload = resolve;
      script.onerror = function () {
        loading = null; // allow a retry
        reject(new Error('Nie udało się wczytać mapy InPost. Sprawdź połączenie.'));
      };
      document.head.appendChild(script);
    });
    return loading;
  }

  function init(root) {
    if (root.dataset.gzGotowe === '1') return;
    root.dataset.gzGotowe = '1';

    var token = root.dataset.token;
    var modal = root.querySelector('[data-gz-modal]');
    var mapHost = root.querySelector('[data-gz-map]');
    var openButton = root.querySelector('[data-gz-open]');
    var closeButton = root.querySelector('[data-gz-close]');
    var errorEl = root.querySelector('[data-gz-error]');

    // Any missing piece means the picker cannot work. Bail out visibly rather
    // than wiring a button that throws on click: a null modal took the whole
    // script down once the markup drifted.
    if (!token || !modal || !mapHost || !openButton || !closeButton || !errorEl) {
      root.hidden = true;
      setLock(false);
      return;
    }

    function showError(message) {
      errorEl.textContent = message;
      errorEl.hidden = false;
    }

    function clearError() {
      errorEl.hidden = true;
    }

    root.querySelectorAll('[data-gz-method]').forEach(function (radio) {
      radio.addEventListener('change', function () {
        renderPanels();
        var method = lockerMode() ? LOCKER : COURIER;
        var attributes = { 'Metoda dostawy': method };
        // Switching back to courier clears the point, so an address delivery
        // never carries a stale locker id into the order.
        if (method === COURIER) {
          attributes.Paczkomat = '';
          attributes['Paczkomat adres'] = '';
          renderPoint('', '');
        }
        saveAttributes(attributes).catch(function (e) {
          showError(e.message);
        });
      });
    });

    var callbackName = 'gzPaczkomatWybrany' + ++instanceCount;
    window[callbackName] = function (point) {
      var data = (point && point.detail) || point || {};
      var code = data.name || '';
      var address = '';
      if (data.address) {
        address = [data.address.line1, data.address.line2].filter(Boolean).join(', ');
      }
      if (!code) {
        showError('Nie udało się odczytać wybranego punktu. Spróbuj ponownie.');
        return;
      }

      renderPoint(code, address);
      clearError();
      if (modal.open) modal.close();

      saveAttributes({
        'Metoda dostawy': LOCKER,
        Paczkomat: code,
        'Paczkomat adres': address,
      }).catch(function (e) {
        showError(e.message + ' Wybór może nie zapisać się w zamówieniu.');
      });
    };

    openButton.addEventListener('click', function () {
      clearError();
      openButton.disabled = true;
      loadGeowidget()
        .then(function () {
          if (!mapHost.querySelector('inpost-geowidget')) {
            var widget = document.createElement('inpost-geowidget');
            widget.setAttribute('token', token);
            widget.setAttribute('language', 'pl');
            widget.setAttribute('config', 'parcelcollect');
            widget.setAttribute('onpoint', callbackName);
            mapHost.appendChild(widget);
          }
          modal.showModal();
        })
        .catch(function (e) {
          showError(e.message);
        })
        .finally(function () {
          openButton.disabled = false;
        });
    });

    closeButton.addEventListener('click', function () {
      modal.close();
    });

    // Clicking the backdrop closes the dialog — <dialog> does not do this itself.
    modal.addEventListener('click', function (e) {
      if (e.target === modal) modal.close();
    });
  }

  function initConsent() {
    consentBoxes().forEach(function (box) {
      if (box.dataset.gzGotowe === '1') return;
      box.dataset.gzGotowe = '1';
      box.addEventListener('change', function () {
        refreshLock();
        saveAttributes({ 'Akceptacja regulaminu': box.checked ? 'tak' : '' }).catch(
          function () {
            /* the checkbox still governs the button; only the record is lost */
          }
        );
      });
    });
  }

  function initAll() {
    try {
      widgets().forEach(init);
      initConsent();
      renderPanels();
    } catch (e) {
      setLock(false);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
  document.addEventListener('shopify:section:load', initAll);

  // Dawn empties and rebuilds the whole item list on every quantity change, so
  // the block collapses to nothing and springs back — the page jumps under the
  // cursor mid-click. It flags the update with `cart__items--disabled`, which is
  // enough to hold the old height until the new markup is in place.
  function holdHeight(section) {
    if (!section) return;
    var frozen = false;

    new MutationObserver(function (records) {
      records.forEach(function (m) {
        var cel = m.target;
        if (!cel.classList || !cel.classList.contains) return;
        var trwa = cel.classList.contains('cart__items--disabled');

        if (trwa && !frozen) {
          frozen = true;
          section.style.minHeight = section.offsetHeight + 'px';
        } else if (!trwa && frozen) {
          frozen = false;
          // One frame later: releasing in the same tick measures the old layout
          // and the jump comes back.
          requestAnimationFrame(function () {
            section.style.minHeight = '';
          });
        }
      });
    }).observe(section, { attributes: true, subtree: true, attributeFilter: ['class'] });
  }

  holdHeight(document.querySelector('[id$="__cart-items"]'));

  // Quantity and remove rewrite the cart sections in place. Watching them is the
  // only signal the theme gives that the totals moved.
  //
  // Watch the SECTION wrapper, not #main-cart-items: the latter is part of what
  // gets swapped, so after the first update the observer was left holding a
  // detached node and every later change went unnoticed. The section element
  // itself survives every swap.
  var cartSection = document.querySelector('[id$="__cart-items"]');
  if (cartSection && window.MutationObserver) {
    var pending = false;
    new MutationObserver(function () {
      if (pending) return;
      pending = true;
      requestAnimationFrame(function () {
        pending = false;
        syncFromCart();
      });
    }).observe(cartSection, { childList: true, subtree: true });
  }

  // The cart drawer replaces its own markup after every cart change, which drops
  // our listeners and lock state — rebuild once the swap settles.
  var drawer = document.querySelector('cart-drawer');
  if (drawer && window.MutationObserver) {
    // Rebuilding mutates the DOM itself (hiding panels, toggling classes), so
    // without a guard the observer would keep waking itself up.
    var rebuilding = false;
    new MutationObserver(function () {
      if (rebuilding) return;
      rebuilding = true;
      requestAnimationFrame(function () {
        try {
          initAll();
        } finally {
          rebuilding = false;
        }
      });
    }).observe(drawer, { childList: true, subtree: true });
  }
})();
